import { useState, useEffect } from 'react';
import { OptionsVolumePoint, VolumeTimeframe } from '../types/StockData';

interface UseOptionsVolumeDataResult {
  data: OptionsVolumePoint[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const getTimeframeDays = (timeframe: VolumeTimeframe): number => {
  switch (timeframe) {
    case '1W': return 7;
    case '1M': return 31;
    case '3M': return 93;
    case '6M': return 186;
    default: return 7;
  }
};

export const useOptionsVolumeData = (
  ticker: string,
  timeframe: VolumeTimeframe
): UseOptionsVolumeDataResult => {
  const [data, setData] = useState<OptionsVolumePoint[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!ticker) {
      setData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const days = getTimeframeDays(timeframe);
      const url = `https://tradewithaiapi20250728175655.azurewebsites.net/api/whales/optionsvolume/${ticker.toLowerCase()}/${days}`;
      
      console.log(`Fetching options volume data from: ${url}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: OptionsVolumePoint[] = await response.json();
      
      console.log(`Received ${result.length} options volume data points for ${ticker}`);
      
      setData(result);
    } catch (err) {
      console.error('Error fetching options volume data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch options volume data');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [ticker, timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = () => {
    fetchData();
  };

  return {
    data,
    loading,
    error,
    refetch,
  };
};
